import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaStar, FaCheckCircle, FaTimesCircle, FaHotel, FaPaw } from "react-icons/fa";
import "../css/styles.css";

const NorthernTanzania = () => {
  return (
    <div className="safari-detail-page">
      {/* ===== HERO ===== */}
      <section className="safari-detail-hero">
        <div className="safari-detail-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776348825/7_Day_Northern_Tanzania_Safari_mvpqrt.jpg"
            alt="Northern Tanzania Safari"
          />
          <div className="safari-detail-hero-overlay" />
        </div>
        <div className="safari-detail-hero-content">
          <span className="safari-detail-eyebrow">Tanzania Safari</span>
          <h1>7 Day Northern Tanzania Safari</h1>
          <p className="safari-detail-hero-sub">
            Tarangire, Serengeti, and the Ngorongoro Crater — Tanzania's most iconic safari destinations in one unforgettable journey.
          </p>
          <div className="safari-detail-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <section className="safari-detail-overview">
        <div className="safari-detail-section-header">
          <span className="section-tag">Safari Overview</span>
          <h2>The Heart of Northern Tanzania</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-text">
          <p>
            This safari takes you through the heart of Northern Tanzania, where some of Africa's most famous wildlife parks lie side by side. From the giant baobab trees and elephant herds of Tarangire National Park to the endless wildlife plains of Serengeti National Park, and finally the breathtaking Ngorongoro Crater, every day reveals a new landscape and a new story of the wild.
          </p>
          <p>
            You will spend your days exploring these remarkable parks on game drives, watching lions resting under acacia trees, elephants walking slowly across the savannah, and herds of zebras and antelope grazing in the open plains.
          </p>
          <p>
            This journey is perfect for travelers who want to experience Tanzania's most iconic safari destinations in one unforgettable adventure.
          </p>
        </div>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="safari-detail-highlights">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Highlights</span>
          <h2>Safari Highlights</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-highlights-list">
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Game drives in the wildlife-rich Serengeti National Park</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Explore Tarangire National Park, famous for elephants and baobab trees</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Descend into the spectacular Ngorongoro Crater, a natural wildlife sanctuary</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Excellent chances to see lions, elephants, giraffes, zebras, and many other animals</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Beautiful landscapes ranging from savannah plains to green highlands</p>
          </div>
        </div>
      </section>

      {/* ===== AT A GLANCE ===== */}
      <section className="safari-detail-glance">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Quick Info</span>
          <h2>Safari at a Glance</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-glance-grid">
          <div className="safari-detail-glance-card">
            <FaClock className="safari-detail-glance-icon" />
            <h4>Duration</h4>
            <p>7 Days / 6 Nights</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Start / End</h4>
            <p>Arusha</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Destinations</h4>
            <p>Tarangire &ndash; Serengeti &ndash; Ngorongoro</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaCalendarAlt className="safari-detail-glance-icon" />
            <h4>Best Time to Travel</h4>
            <p>Year-round (best Jun &ndash; Oct)</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaPaw className="safari-detail-glance-icon" />
            <h4>Safari Style</h4>
            <p>Classic Wildlife Safari</p>
          </div>
        </div>
      </section>

      {/* ===== ITINERARY ===== */}
      <section className="safari-detail-itinerary">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Day by Day</span>
          <h2>Day-by-Day Itinerary</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-days">
          {/* Day 1 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">1</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 1 — Arrival in Arusha</h3>
              <p>
                Arrive at Kilimanjaro International Airport, where your Nyatwende representative will warmly welcome you to Tanzania. From here, you will transfer to your lodge in Arusha, a peaceful town at the foot of Mount Meru and the gateway to Northern Tanzania's famous safari parks.
              </p>
              <p>
                Spend the rest of the day relaxing after your journey, enjoying the calm gardens and fresh mountain air.
              </p>
              <p className="safari-detail-day-note">Overnight in Arusha.</p>
            </div>
          </div>

          {/* Day 2 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">2</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 2 — Arusha to Tarangire National Park</h3>
              <p>
                After breakfast, begin your safari journey with a scenic drive to Tarangire National Park, known for its towering baobab trees and large elephant herds.
              </p>
              <p>
                Enjoy your first game drive through the park's golden savannah, where elephants roam freely and lions rest under acacia trees. The Tarangire River attracts many animals, making it an excellent place to see wildlife gathering to drink.
              </p>
              <p>
                Arrive at your lodge in time to relax before dinner while listening to the sounds of the African wilderness.
              </p>
              <p className="safari-detail-day-note">Overnight in Tarangire.</p>
            </div>
          </div>

          {/* Day 3 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">3</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 3 — Tarangire to Serengeti National Park</h3>
              <p>
                After breakfast, travel north toward the legendary Serengeti National Park. The journey itself is part of the adventure as the landscape slowly opens into vast, endless plains.
              </p>
              <p>
                Arrive in the Serengeti and begin your first game drive through one of Africa's most famous wildlife reserves. Lions, cheetahs, giraffes, and large herds of zebras are commonly seen across the plains.
              </p>
              <p>
                As the sun sets over the savannah, arrive at your lodge or camp for dinner and a relaxing evening.
              </p>
              <p className="safari-detail-day-note">Overnight in the Serengeti.</p>
            </div>
          </div>

          {/* Day 4 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">4</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 4 — Full Day in Serengeti</h3>
              <p>
                Spend the day exploring the extraordinary wildlife of the Serengeti. Morning and afternoon game drives give you the best chances to see predators in action and large herds of wildlife moving across the plains.
              </p>
              <p>
                Depending on the season, you may witness part of the great wildebeest migration, one of nature's most spectacular events.
              </p>
              <p>
                An optional sunrise hot air balloon safari offers a magical perspective of the Serengeti as the day begins.
              </p>
              <p className="safari-detail-day-note">Overnight in the Serengeti.</p>
            </div>
          </div>

          {/* Day 5 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">5</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 5 — Serengeti to Ngorongoro Conservation Area</h3>
              <p>
                After breakfast, enjoy a final morning game drive in the Serengeti before continuing toward the Ngorongoro Conservation Area.
              </p>
              <p>
                The scenery changes as you travel toward the highlands, where forests and rolling hills surround the famous Ngorongoro Crater. This ancient volcanic caldera is home to a remarkable concentration of wildlife, including elephants, lions, buffalo, and sometimes even the rare black rhino.
              </p>
              <p>
                Arrive at your lodge near the crater rim and enjoy spectacular views over this natural wonder.
              </p>
              <p className="safari-detail-day-note">Overnight in Ngorongoro.</p>
            </div>
          </div>

          {/* Day 6 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">6</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 6 — Ngorongoro Crater Safari</h3>
              <p>
                After an early breakfast, descend into the Ngorongoro Crater for a memorable game drive. The crater floor is often called Africa's natural wildlife sanctuary, with animals living here year-round thanks to the permanent water sources and fertile grasslands.
              </p>
              <p>
                You may see lions resting in the grass, elephants wandering through forests, and flamingos coloring the soda lakes pink.
              </p>
              <p>
                Enjoy a picnic lunch inside the crater before returning to your lodge in the afternoon.
              </p>
              <p className="safari-detail-day-note">Overnight in Ngorongoro.</p>
            </div>
          </div>

          {/* Day 7 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">7</div>
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 7 — Ngorongoro to Arusha / Departure</h3>
              <p>
                After breakfast, drive back to Arusha, enjoying the final views of Tanzania's beautiful countryside along the way.
              </p>
              <p>
                Upon arrival, transfer to Kilimanjaro International Airport for your onward journey.
              </p>
              <p className="safari-detail-day-note">
                Your Tanzania safari ends here, leaving you with unforgettable memories of wildlife, vast landscapes, and the beauty of East Africa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCOMMODATION ===== */}
      <section className="safari-detail-accommodation">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Where You'll Stay</span>
          <h2>Accommodation Examples</h2>
          <div className="safari-detail-line" />
        </div>

        {/* Arusha */}
        <h3 className="safari-detail-accom-region-title">Arusha</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">City Hotels</div>
            <ul>
              <li><FaHotel /> Arusha Serena Hotel</li>
              <li><FaHotel /> Gran Meli&aacute; Arusha</li>
            </ul>
          </div>
        </div>

        {/* Tarangire */}
        <h3 className="safari-detail-accom-region-title">Tarangire</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Tarangire Sopa Lodge</li>
              <li><FaHotel /> Tarangire Simba Lodge</li>
            </ul>
          </div>
        </div>

        {/* Serengeti */}
        <h3 className="safari-detail-accom-region-title">Serengeti</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Serengeti Serena Safari Lodge</li>
              <li><FaHotel /> Serengeti Simba Lodge</li>
            </ul>
          </div>
        </div>

        {/* Ngorongoro */}
        <h3 className="safari-detail-accom-region-title">Ngorongoro</h3>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Safari Lodges</div>
            <ul>
              <li><FaHotel /> Ngorongoro Serena Safari Lodge</li>
              <li><FaHotel /> Ngorongoro Sopa Lodge</li>
            </ul>
          </div>
        </div>

        <p className="safari-detail-accom-note">
          All lodges are carefully selected for comfort, location, and authentic safari experiences. Similar lodges of the same standard may be used depending on availability.
        </p>
      </section>

      {/* ===== INCLUDED / EXCLUDED ===== */}
      <section className="safari-detail-inclusions">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">What to Expect</span>
          <h2>What's Included</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-inclusions-grid">
          <div className="safari-detail-inclusions-card included">
            <h3><FaCheckCircle /> Included</h3>
            <ul>
              <li>Airport transfers in Arusha</li>
              <li>Transport in a safari vehicle with professional driver-guide</li>
              <li>Park entrance fees for all national parks</li>
              <li>Accommodation during the safari</li>
              <li>Meals as specified in the itinerary</li>
              <li>Bottled drinking water during game drives</li>
            </ul>
          </div>
          <div className="safari-detail-inclusions-card excluded">
            <h3><FaTimesCircle /> Not Included</h3>
            <ul>
              <li>International flights</li>
              <li>Travel insurance</li>
              <li>Visa fees</li>
              <li>Optional activities such as hot air balloon safari</li>
              <li>Personal expenses</li>
              <li>Tips and gratuities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="safari-detail-cta">
        <div className="safari-detail-cta-content">
          <h2>Customize This Safari</h2>
          <p>
            This safari can easily be tailored to match your travel style. Add extra nights in the Serengeti, upgrade to luxury lodges or tented camps, or extend your trip with a Zanzibar beach escape.
          </p>
          <div className="safari-detail-cta-buttons">
            <Link to="/contact?safari=7 Day Northern Tanzania Safari" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/tours" className="hero-cta secondary">
              View More Safaris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NorthernTanzania;
